import React, { useEffect, useMemo, useRef, useState } from 'react'
import { isEqual, uniqBy } from 'lodash'

import {
  Alert,
  Button,
  hasValue,
  ListLoading,
  type StandardTable,
  useIsMounted,
} from '../../../module'
import { getDataGroups } from '../helperFunctions'
import { backgroundColor, color } from '../StandardTable'
import type { GroupHeader, TableSortingProps } from '../StandardTable.models'
import {
  type ConfigItemType,
  type StandardTableProps,
} from '../StandardTable.models'
import { type SelectDisplayOption } from '../../Selects/SelectDisplay/SelectDisplay'
import MobileColumnPicker from './MobileColumnPicker'
import MobileTableHeader from './MobileTableHeader'
import MobileTableRow from './MobileTableRow'
import { Icon, Tooltip } from '../../../module'
import styles from './_mobile-table.module.scss'
import checkboxStyles from '../_table.module.scss'
import {
  checkedData,
  type DataItemExtended,
  useHandleCheck,
  useIsChecked,
} from '../helperCheckboxFunctions'
import { c } from '../../../translations/LibraryTranslationService'

type TableProps = React.ComponentProps<typeof StandardTable>

type MobileTableProps<
  DataItem,
  ConfigItem extends ConfigItemType<DataItem, Record<string, unknown>>,
  FilterOption,
> = Pick<
  StandardTableProps<DataItem, ConfigItem, FilterOption>,
  | 'data'
  | 'activeFilters'
  | 'hasMore'
  | 'successStatus'
  | 'getData'
  | 'loading'
  | 'hasData'
  | 'groups'
  | 'showGroups'
  | 'disableCheckAll'
> &
  TableSortingProps & {
    localStorageName: string
    config: TableProps['config']
    dataKey: TableProps['dataKey']
    totalRowKey: keyof DataItem
    mainColumnClassName?: string
    tableHeaderProps?: TableProps['tableHeaderProps']
    hasCheckboxes?: boolean
    handleCheckedBoxes?: TableProps['handleCheckedBoxes']
    isDefaultCheckedAll?: boolean
    enableCheckAll?: boolean
    totalCheckboxOptions?: number | null
    isResetCheckboxes?: boolean
    disableCheckboxes?: (data: DataItem) => boolean
    checkboxSelectOptions?: {
      options?: SelectDisplayOption[]
      callout: (option: SelectDisplayOption) => void
      active?: string
    }
  }

const headerStyle = (hasTotalRow: boolean, heightOfHeaderRow: number) =>
  ({
    position: 'sticky',
    top: hasTotalRow
      ? /** The height of the header + set distance to top (53px - set in `header` css class) + width of the total row */
        heightOfHeaderRow + 53 + 48 + 'px'
      : /** The height of the header + set distance to top (53px - set in `header` css class)  */
        heightOfHeaderRow + 53 + 'px',
    zIndex: '3',
  }) as React.CSSProperties

const MobileTable = <
  DataItem extends {
    groupDataKey?: string
  },
  ConfigItem extends ConfigItemType<DataItem, Record<string, unknown>>,
  FilterOption extends Record<string, unknown>,
>({
  config,
  sort,
  sortBy,
  localStorageName,
  activeFilters,
  data,
  dataKey,
  enableCheckAll = false,
  disableCheckAll = false,
  groups,
  handleCheckedBoxes,
  showGroups,
  hasMore,
  isDefaultCheckedAll = false,
  successStatus,
  getData,
  loading,
  hasData,
  totalRowKey,
  hasCheckboxes,
  mainColumnClassName = '',
  tableHeaderProps,
  totalCheckboxOptions,
  noSort,
  isResetCheckboxes,
  disableCheckboxes,
  checkboxSelectOptions,
}: MobileTableProps<DataItem, ConfigItem, FilterOption>): React.JSX.Element => {
  const infiniteLoadRef = useRef<HTMLDivElement>(null),
    scrollPositionRef = useRef<HTMLDivElement>(null),
    headerPositionRef = useRef<HTMLDivElement>(null),
    groupHeaderRefsArray = useRef<(HTMLDivElement | null)[]>([]),
    mounted = useIsMounted()
  const secondaryIndex =
    (config.findIndex((c) => c.mainColumn) > -1
      ? config.findIndex((c) => c.mainColumn)
      : 0) + 1

  const mainColumn = config.find((c) => c.mainColumn) ?? config[0]

  // Find a valid secondary column index that isn't the main column
  const getValidSecondaryIndex = () => {
    let validIndex = secondaryIndex
    // Ensure the secondary index is not the same as the main column index
    if (validIndex === config.findIndex((c) => c === mainColumn)) {
      // Look for the next available column that isn't the main column
      const nextValidColumn = config.find((c, i) => i !== 0 && c !== mainColumn)
      validIndex = nextValidColumn
        ? config.findIndex((c) => c === nextValidColumn)
        : 1

      // If we couldn't find another valid column, try the next index
      if (validIndex === config.findIndex((c) => c === mainColumn)) {
        validIndex = (validIndex + 1) % config.length
      }
    }
    return validIndex
  }

  const secondaryColumnIndex = getValidSecondaryIndex()

  const [activeSecondaryColumn, setActiveSecondaryColumn] = useState(
      config[secondaryColumnIndex]?.label,
    ),
    [displayData, setDisplayData] =
      useState<DataItemExtended<DataItem>[]>(data),
    [heightOfHeaderRow, setHeightOfHeaderRow] = useState<number>(0),
    [sortColumnsCount, setSortColumnsCount] = useState(0)

  const previousData = useRef(data)
  const previousSortBy = useRef(sortBy)

  const updateActiveColumn = (header: string) => {
    setActiveSecondaryColumn(header)
  }

  ///////////////////////////////////////////////////////////////////////////
  // CHECKBOX functionality
  ///////////////////////////////////////////////////////////////////////////
  const [checkedBoxes, setCheckedBoxes] = useState<
      DataItemExtended<DataItem>[]
    >([]),
    [checkAll, setCheckAll] = useState<boolean>(isDefaultCheckedAll),
    [checkAllLoaded, setCheckAllLoaded] =
      useState<boolean>(isDefaultCheckedAll),
    [makeCallout, setMakeCallout] = useState<'' | 'single' | 'checkAll'>('')

  const handleCheck = useHandleCheck({
    checkedBoxes,
    setCheckedBoxes,
    setDisplayData,
    data,
    dataKey,
    totalRowKey,
    setCheckAll,
    setCheckAllLoaded,
    enableCheckAll,
    setMakeCallout,
    mounted: mounted(),
  })

  const { someChecked, allChecked } = useMemo(() => {
    const { someChecked, allChecked } = checkedData({
      data,
      checkedBoxes,
      totalRowKey,
      dataKey,
    })
    if (allChecked) {
      setCheckAllLoaded(true)
    }
    return { someChecked, allChecked }
  }, [checkedBoxes, data, dataKey, totalRowKey])

  const handleCheckAllOrNone = (checkAll: boolean) => {
    setCheckAllLoaded(checkAll)
    const updatedCheckedBoxes = checkAll ? [...data] : []
    setCheckedBoxes(updatedCheckedBoxes?.filter((item) => !item[totalRowKey]))
    setDisplayData((prevData) =>
      prevData.map((item) => ({
        ...item,
        checked: checkAll,
      })),
    )
    handleCheckedBoxes?.(updatedCheckedBoxes)
  }

  const checkboxProps = {
    checkAll: checkAll || checkAllLoaded,
    enableCheckAll,
    handleCheck,
    handleCheckAllOrNone,
    loading: false,
    someChecked,
  }

  const isChecked = useIsChecked({ checkedBoxes, dataKey })

  const CheckAllBanner = () => {
    return checkAll ? (
      <div className={checkboxStyles.flexAlignCenter}>
        <span className={`${checkboxStyles.fwBold}`}>
          {`${c('all')} ${totalCheckboxOptions ?? ''} ${c('selected')}.`}
        </span>
        <Button
          as='button'
          styleType='text-blue'
          onClick={() => {
            setCheckAll(false)
            handleCheckAllOrNone(false)
          }}
          style={{ textDecoration: 'underline' }}
        >
          {c('clearSelection')}
        </Button>
      </div>
    ) : (
      <div className={checkboxStyles.flexAlignCenter}>
        <span>
          <span
            className={`${checkboxStyles.fwBold}`}
          >{`${checkedBoxes.length} ${c('selected')}.`}</span>
          <span
            className={`${checkboxStyles.paddingX4} ${checkboxStyles.fwRegular}`}
          >
            {`(${c('allCurrentlyLoaded')})`}
          </span>
        </span>
        <Button
          as='button'
          styleType='text-blue'
          onClick={() => {
            setCheckAll(true)
            handleCheck({
              checkboxValue: true,
              checkedItemData: data[0],
              name: 'all',
            })
          }}
          style={{ textDecoration: 'underline' }}
        >
          {`${c('selectAll')} ${totalCheckboxOptions ?? ''}`}
        </Button>
      </div>
    )
  }

  useEffect(() => {
    // must wait for checkboxes to update on state before making call to handleCheckedBoxes
    if (makeCallout) {
      if (makeCallout === 'checkAll') {
        handleCheckedBoxes?.(checkedBoxes, checkAllLoaded)
      }
      if (makeCallout === 'single') {
        handleCheckedBoxes?.(checkedBoxes)
      }
      setMakeCallout('')
    }
  }, [checkAllLoaded, checkedBoxes, handleCheckedBoxes, makeCallout])

  // Set checkAll when all items are loaded and checkAllLoaded is true
  useEffect(() => {
    // If all currently loaded items are selected and we've loaded all data
    if (checkAllLoaded && !hasMore && !checkAll && !loading) {
      setCheckAll(true)
    }
  }, [checkAllLoaded, hasMore, checkAll, loading])

  useEffect(() => {
    // Get the main column's name and label for comparison
    const mainColumnName = mainColumn?.name
    const mainColumnLabel = mainColumn?.label

    // Only update secondary column if the sort column isn't the main column
    if (sortBy?.prop && sortBy.prop !== mainColumnName) {
      const matchingColumn = config
        .filter((c) => !c.mainColumn && c.label !== mainColumnLabel) // Exclude main column
        .find((c) => c.name === sortBy.prop)

      if (matchingColumn) {
        updateActiveColumn(matchingColumn.label)
      }
    }

    const configWithValuesCount = config.reduce(
      (r, o) => r + +!Object.values(o).includes(''),
      0,
    )
    setSortColumnsCount(configWithValuesCount)
  }, [config, sortBy?.prop, mainColumn])

  const isAllColumnsNoSort = config?.every((cn) => cn?.noSort)

  // Select activeColumn ensuring it's never the same as mainColumn
  const activeColumn = useMemo(() => {
    // First try to find the column with the exact activeSecondaryColumn label
    let column = config.find(
      (h) => h.label === activeSecondaryColumn && h.label !== mainColumn.label,
    )

    // If not found or if it matched the main column, find another valid column
    if (!column || column === mainColumn) {
      // Find any column that's not the main column
      column = config.find(
        (c) => c !== mainColumn && c.label !== mainColumn.label,
      )

      // If still not found (unlikely case with only one column), use the secondaryIndex as fallback
      if (!column) {
        column =
          config[
            secondaryColumnIndex === 0
              ? 1 % config.length
              : secondaryColumnIndex
          ]
      }

      // Update state with the corrected column label
      if (column && column.label !== activeSecondaryColumn) {
        setActiveSecondaryColumn(column.label)
      }
    }

    return column || config[secondaryColumnIndex]
  }, [config, activeSecondaryColumn, mainColumn, secondaryColumnIndex])

  useEffect(() => {
    if (infiniteLoadRef.current && hasMore && successStatus) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
              getData?.()
            }
          })
        },
        { threshold: 0.5 },
      )

      observer.observe(infiniteLoadRef.current)

      return () => {
        observer.disconnect()
      }
    }
  }, [hasMore, successStatus, getData])

  useEffect(() => {
    /** SET DATA GROUPS WITH VALUES */
    if (showGroups && groups) {
      const groupData: DataItem[] = getDataGroups(
        data,
        groups,
        { hasCheckboxes },
        totalRowKey,
      )
      setDisplayData(groupData)
    } else {
      setDisplayData(data)
    }
  }, [data, groups, showGroups, totalRowKey, hasCheckboxes])

  useEffect(() => {
    /** NOTE: get current header row height */
    if (headerPositionRef.current) {
      const currentHeaderHeight =
        headerPositionRef.current.getBoundingClientRect().height
      const hasChanged = heightOfHeaderRow !== currentHeaderHeight
      if (hasChanged) {
        setHeightOfHeaderRow(currentHeaderHeight)
      }
    }
  }, [heightOfHeaderRow, sort])

  // Reset checkboxes effect
  useEffect(() => {
    const resetCheckboxes = () => {
      setCheckAllLoaded(false)
      setCheckedBoxes([])
      if (enableCheckAll) {
        setCheckAll(false)
      }
    }

    if (isResetCheckboxes) {
      resetCheckboxes()
    }
  }, [isResetCheckboxes, enableCheckAll])

  // Initialize checked state when isDefaultCheckedAll is true
  useEffect(() => {
    if (isDefaultCheckedAll) {
      setCheckedBoxes(data?.filter((dataItem) => !dataItem[totalRowKey]))
    }
  }, [data, isDefaultCheckedAll, totalRowKey])

  // Handle data changes
  useEffect(() => {
    // check new data for checkboxes previously selected to update this component state
    const dataRecordOne: DataItem & { checked?: boolean } = data?.[0]
    if (dataRecordOne?.checked !== undefined) {
      if (data?.length && hasCheckboxes) {
        let noCheckedValues = 0
        const initialCheckedBoxes = data.filter(
          (item: DataItem & { checked?: boolean }) => {
            if (item && !('checked' in item)) {
              noCheckedValues++
            }
            return item?.checked
          },
        )
        if (data?.length !== noCheckedValues) {
          const checkedBoxesWithoutTotalRowKey = initialCheckedBoxes.filter(
            (item) => !item[totalRowKey],
          )
          const checkedBoxesChanged = !isEqual(
            checkedBoxesWithoutTotalRowKey,
            checkedBoxes,
          )
          if (checkedBoxesChanged) {
            setCheckedBoxes(checkedBoxesWithoutTotalRowKey)
          }
        }
      }

      // when data changes, check if there are items and if all items are checked
      setCheckAllLoaded(
        data?.length > 0 &&
          data.every((item: DataItem & { checked?: boolean }) => item?.checked),
      )
    }

    if (enableCheckAll) {
      let hasNewData = true
      if (!isEqual(data, previousData.current)) {
        // Check if only the sort changed
        const onlySortChanged = !isEqual(sortBy, previousSortBy.current)

        if (!onlySortChanged || !checkAll) {
          const wasAdditionalDataLoaded = previousData.current.every(
            (prevItem) => data.some((newItem) => isEqual(prevItem, newItem)),
          )

          // Only reset if it's not just a sort change and no additional data was loaded
          if (!wasAdditionalDataLoaded) {
            setCheckAllLoaded(false)
            setCheckedBoxes([])
            if (enableCheckAll) {
              setCheckAll(false)
            }
            hasNewData = false
          }
        }

        previousData.current = data
        previousSortBy.current = sortBy
      }

      // if checkAll is true then all new data should be selected
      if (hasNewData && checkAll) {
        const updatedCheckedBoxes = uniqBy([...checkedBoxes, ...data], dataKey)
        if (!isEqual(updatedCheckedBoxes, checkedBoxes)) {
          setCheckedBoxes(updatedCheckedBoxes)
        }
      }
    }
  }, [
    checkAll,
    checkedBoxes,
    data,
    dataKey,
    hasCheckboxes,
    enableCheckAll,
    setCheckedBoxes,
    sortBy,
    totalRowKey,
  ])

  // Initialize the refs array with the correct number of refs
  useEffect(() => {
    if (groups) {
      groupHeaderRefsArray.current = Array(groups.length).fill(null)
    }
  }, [groups])

  return (
    <>
      {sortColumnsCount >= 3 && !isAllColumnsNoSort && sort && sortBy && (
        <MobileColumnPicker
          config={config}
          localStorageName={localStorageName}
          sort={sort}
          sortBy={sortBy}
          updateActiveColumn={updateActiveColumn}
          activeSecondaryColumn={activeSecondaryColumn}
          scrollPositionRef={scrollPositionRef}
          mainColumnClassName={mainColumnClassName}
        />
      )}

      {enableCheckAll && checkAllLoaded && !someChecked && hasMore ? (
        <Alert type='info' text={<CheckAllBanner />} />
      ) : null}

      <div className={styles.dataTable}>
        {/******************* TABLE HEADER WITH 2 COLUMNS ************************/}
        <MobileTableHeader
          headerPositionRef={headerPositionRef}
          checkboxProps={hasCheckboxes ? checkboxProps : undefined}
          mainColumn={mainColumn}
          activeColumn={activeColumn}
          hasData={hasData}
          {...(!noSort && sort && sortBy
            ? { sort, sortBy }
            : {
                noSort: true,
              })}
          localStorageName={localStorageName}
          activeFilters={activeFilters}
          activeSecondaryColumn={activeSecondaryColumn}
          tableHeaderActiveFilters={
            tableHeaderProps?.columnFilterProps?.activeFilters
          }
          checkboxSelectOptions={checkboxSelectOptions}
          disableCheckAll={disableCheckAll}
        />
        {!loading ? (
          hasData && (
            <div>
              {/*********** DATA ROWS ***********/}
              {displayData?.map((data: GroupHeader<DataItem>, index) => (
                <React.Fragment key={`${index}_${data?.[dataKey]}`}>
                  {data?.isGroupHeader ? (
                    <div
                      ref={(ref) => {
                        // Store references of only those group headers which have header details
                        // Reference of group 1 at index 0, group 2 at index 1 and so on.
                        if (data.groupNum) {
                          groupHeaderRefsArray.current[data.groupNum - 1] = ref
                        }
                      }}
                      className={`${color[data.type ?? 'gray']} ${
                        backgroundColor[data.type ?? 'gray']
                      } ${
                        data?.displayHeaderData ? '' : styles.headerDataPadding
                      } `}
                      style={headerStyle(!!totalRowKey, heightOfHeaderRow)}
                      key={`${data.groupHeader}-${index}`}
                    >
                      {data?.displayHeaderData ? (
                        <MobileTableRow
                          key={data?.[dataKey] ?? index}
                          activeColumn={activeColumn}
                          mainColumn={mainColumn}
                          columns={config}
                          data={data}
                          {...(sort && sortBy
                            ? { sortBy: { ...sortBy, prop: '' } }
                            : {})} // don't highlight the header row's sort column cell
                          rowIndex={index}
                          totalRowKey={totalRowKey}
                          scrollPositionRef={scrollPositionRef}
                          headerPositionRef={headerPositionRef}
                          groupHeaderRefsArray={groupHeaderRefsArray.current}
                          hasCheckboxes={hasCheckboxes}
                          checked={allChecked}
                        />
                      ) : (
                        <span className={styles.displayFlex}>
                          {typeof data.groupHeader === 'function'
                            ? data.groupHeader(data.groupNum ?? 0)
                            : data.groupHeader}
                          {data.tooltipContent && (
                            <Tooltip
                              position='auto'
                              tooltipContent={data.tooltipContent}
                            >
                              <Icon
                                icon='info'
                                className={styles.marginLeftIcon}
                                iconSize='12px'
                                color='dark-blue'
                              />
                            </Tooltip>
                          )}
                        </span>
                      )}
                    </div>
                  ) : (
                    <MobileTableRow
                      key={data?.[dataKey] ?? index}
                      activeColumn={activeColumn}
                      mainColumn={mainColumn}
                      columns={config}
                      data={data}
                      sortBy={sortBy}
                      rowIndex={index}
                      totalRowKey={totalRowKey}
                      scrollPositionRef={scrollPositionRef}
                      headerPositionRef={headerPositionRef}
                      hasCheckboxes={hasCheckboxes}
                      checkboxCallout={handleCheck}
                      checked={checkAllLoaded || hasValue(isChecked(data))}
                      disabled={disableCheckboxes?.(data)}
                    />
                  )}
                </React.Fragment>
              ))}
              {hasMore && (
                <div ref={infiniteLoadRef} className={styles.topMargin}>
                  <div>
                    <ListLoading />
                  </div>
                </div>
              )}
            </div>
          )
        ) : (
          <div className={styles.topMargin}>
            <div>
              <ListLoading longList />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default MobileTable
